import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '../utils/api'
import type { Settings } from '../types'

function loadSettingsLocal(): Settings {
  try {
    const data = uni.getStorageSync('devword_settings')
    return data ? JSON.parse(data) : {
      theme: 'dark',
      viewMode: 'list',
      ttsRepeatCount: 3,
      ttsInterval: 1000,
    }
  } catch {
    return {
      theme: 'dark',
      viewMode: 'list',
      ttsRepeatCount: 3,
      ttsInterval: 1000,
    }
  }
}

function saveSettingsLocal(settings: Settings) {
  uni.setStorageSync('devword_settings', JSON.stringify(settings))
}

export const useSettingsStore = defineStore('settings', () => {
  const settings = ref<Settings>(loadSettingsLocal())

  function persist() {
    saveSettingsLocal(settings.value)
    api.setSettings(settings.value).catch(() => {})
  }

  function applyTheme() {
    if (settings.value.theme === 'light') {
      document.documentElement.classList.add('light-mode')
    } else {
      document.documentElement.classList.remove('light-mode')
    }
  }

  function toggleTheme() {
    settings.value.theme = settings.value.theme === 'light' ? 'dark' : 'light'
    persist()
    applyTheme()
  }

  function setTheme(theme: 'dark' | 'light') {
    settings.value.theme = theme
    persist()
    applyTheme()
  }

  function setTTSRepeatCount(count: number) {
    settings.value.ttsRepeatCount = Math.max(1, Math.min(10, count))
    persist()
  }

  function setTTSInterval(interval: number) {
    settings.value.ttsInterval = Math.max(200, Math.min(5000, interval))
    persist()
  }

  async function syncFromServer() {
    try {
      const serverSettings = await api.getSettings()
      if (serverSettings) {
        settings.value = serverSettings
        saveSettingsLocal(serverSettings)
        applyTheme()
      }
    } catch {}
  }

  return {
    settings,
    applyTheme,
    toggleTheme,
    setTheme,
    setTTSRepeatCount,
    setTTSInterval,
    syncFromServer,
  }
})
